/**
 * manager-group.js
 */

import React from 'react';
import { useMediaQuery, Typography, IconButton, List } from '@mui/material';
import { useHistory, useParams, Link } from 'react-router-dom';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import DrawerContent from 'components/menu-drawer/drawer-content';
import MobilePageTitle from 'components/menu-drawer/mobile-page-title';
import IconSection from 'components/icon-section';
import Icon from 'components/icon';
import Button from 'components/button';
import GroupMember from 'components/groups/group-member';
import { managerGroupsRoute } from './manager-groups';
import { ManagerGroupsContext } from './manager-groups-context';


// use wrapper to remount component when group ID changes
export default function ManagerGroup(props) {
  const params = useParams();
  const managerId = +params.managerId;
  const roleId = +params.roleId;

  return <ManagerGroupImplementation
    managerId={managerId}
    roleId={roleId}
    key={`${managerId}:${roleId}`}
    {...props}
  />;
}


function ManagerGroupImplementation(props) {
  const {
    managerId,
    roleId,
    drawerState,
    onClose,
  } = props;
  const { user } = React.useContext(UserContext);
  const { managers, onLeaveManager, onDeleteRequest, onAcceptRequest } = React.useContext(ManagerGroupsContext);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const history = useHistory();
  const managerGroup = React.useMemo(() => (
    managers.find(m => m.manager.id === managerId && m.role.id === roleId)
  ), [managers, managerId, roleId]);
  const title = `${managerGroup?.manager.name}'s ${managerGroup?.role?.name} Group`;
  const requestInfo = (() => {
    if (!managerGroup || managerGroup.dateAccepted) {
      return '';
    }
    // user requesting to be managed
    else if (managerGroup.creator.id === user.id) {
      return `You invited this ${managerGroup.role.name.toLowerCase()} to have access to your account. They haven't accepted yet.`;
    } 
    // supervisor requesting to manage user
    else if (managerGroup.role.id === Types.Role.SUPERVISOR) {
      return `This supervisor is requesting access to your account. Approved supervisors can access your training and deployment records. Use the Accept button to confirm.`;
    } 
    // trainer requesting to manage user
    else if (managerGroup.role.id === Types.Role.TRAINER) {
      return `This trainer is requesting access to your account. Approved trainers can access your training records. Use the Accept button to confirm.`;
    }
  })();


  const backButton = (
    <IconButton
      data-testid="back-button"
      size="small"
      style={{color: 'inherit'}}
      component={Link}
      to={managerGroupsRoute(history)}
      children={<Icon type='nav-back' />}
    />
  );

  
  if (!managerGroup) {
    return null;
  }

  return (
    <>
      <DrawerContentBuilder
        desktopHeader={{ title }}
        mobileAppBar={{
          title: 'Manager Group',
          leftActions: backButton,
        }}
        children={<>          
          <MobilePageTitle title={title} />
          <DrawerContent>

            {/* MEMBERS */}
            {managerGroup?.members &&
            <IconSection iconName='group-members'>
              <Typography variant='subtitle1' gutterBottom>Group Members</Typography>
              <Typography variant='subtext-small' >
                {managerGroup.role.id === Types.Role.SUPERVISOR &&
                <>This supervisor has permission to see and review exercises and deployments for the following handlers.</>}
                {managerGroup.role.id !== Types.Role.SUPERVISOR &&
                <>This trainer has permission to create events and exercises as well as add training notes for the following handlers.</>}
              </Typography>
              <List>{managerGroup.members.map(m => <GroupMember key={m.id} member={m} />)}</List>
            </IconSection>}

            {/* ACCEPT / REMOVE REQUEST */}
            {!managerGroup.dateAccepted &&
            <IconSection iconName='group-accept'>
              <Typography variant='subtext-large' sx={{ color: 'inherit' }}>{requestInfo}</Typography>

              {/* ACCEPT REQUEST */}
              {(managerGroup.creator.id !== user.id) &&
              <Button
                variant='contained'
                color='secondary'
                style={{ display: 'flex', marginTop: 40 }}
                startIcon={<Icon type='group-accept' />}
                children={`Accept ${managerGroup.role.name} Request`}
                onClick={() => onAcceptRequest(managerGroup, drawerState.loading.content.set)}
              />}

              {/* REMOVE REQUEST */}
              <Button
                variant='contained'
                color='secondary'
                style={{ display: 'flex', marginTop: 40 }}
                startIcon={<Icon type='close' />}
                children={`Remove ${managerGroup.role.name} Request`}
                onClick={async () => {
                  if (await onDeleteRequest(managerGroup, drawerState.loading.content.set)) {
                    isMobile ? history.push(managerGroupsRoute(history)) : onClose();
                  }
                }}
              />
            </IconSection>}


            {/* MOBILE ACTIONS */}
            {(isMobile && managerGroup?.dateAccepted) &&
            <IconSection iconName='group-leave'>
              <Typography variant='subtext-large' sx={{ color: 'inherit' }}>
                If you no longer want this {managerGroup.role.name.toLowerCase()} to manage your records then click the button below.
              </Typography>
              <Button
                variant='contained'
                color='secondary'
                style={{ marginTop: 24 }}
                startIcon={<Icon type='close' />}
                children={`Leave ${managerGroup.role.name}`}
                onClick={async () => {
                  if (await onLeaveManager(managerGroup, drawerState.loading.content.set)) {
                    isMobile ? history.push(managerGroupsRoute(history)) : onClose();
                  }
                }}
              />
            </IconSection>}
          </DrawerContent>
        </>}
      />
    </>
  );
}